/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * "Versus yours" on a category card (owner-notes contract #63, ticket
 * dealhunt/docs/sg-owner-notes-versus-yours-ticket.md).
 *
 * DealHunt ships NUMBERS on every v3 card (`axes`, `batteryHours`, `compareSpecs`,
 * `clearbuyProductId`, `currentPriceCents`, `sgReviewScore`); this module turns two
 * products' numbers into the rows a card prints. Both values are always shown, never only the
 * delta: a delta alone hides the scale (MDAQS is /5, ANC is /10). Price and SG score are NOT
 * rows — the card already carries them. No sort, no blended score (#63/§9). Pure: no React,
 * tested in `tests/owner-deltas.spec.ts`.
 */
import { hzText, nearestPoint, type Point } from 'lib/dealhunt/audioChart'

// Chart maths lives in audioChart; `hzText` is re-exported because every band string the
// dialog prints derives through this module (8ec578e0).
export { hzText }

/** One comparable spec from a v3 card / the product-line payload (ticket §A/§C). */
export interface CompareSpec {
  key: string
  label: string
  value: string | number | boolean
  unit: string | null
  dataType: string
  /** Raw list for set-valued specs (codecs) — the joined `value` is display only. */
  values?: string[] | null
}

/** The numbers a product contributes to a comparison — from a v3 card or the PDP payload. */
export interface DeltaNumbers {
  axes: Record<string, number> | null
  batteryHours: number | null
  priceCents: number | null
  sgScore: number | null
  /** SoundGuys lab id — present means the ClearBuy RPC has curves for it. */
  clearbuyProductId?: number | null
  compareSpecs?: CompareSpec[] | null
}

export type Direction = 'up' | 'down' | 'same' | 'neutral'

/** A score axis both sides carry: rendered as two dots on the axis's own scale. */
export interface ScaleRow {
  kind: 'scale'
  key: string
  label: string
  mine: number
  theirs: number
  min: number
  max: number
  direction: Direction
}

/** A score axis only one side carries — the row says so instead of vanishing. */
export interface AbsentRow {
  kind: 'absent'
  key: string
  label: string
  text: string
  /** Rendered on a line of its own under the row — see `.note` in VersusYours.module.css. */
  note?: string
}

/** A text row: "6 h" + "vs your 11 h", optionally with a note ("yours adds LDAC"). */
export interface TextRow {
  kind: 'text'
  key: string
  label: string
  theirs: string
  mine: string
  direction: Direction
  note?: string
}

// No owners row (contract #115): owner counts are the card's own Owners block, not a
// comparison against the declared pair.
export type CompareRow = ScaleRow | AbsentRow | TextRow

/**
 * What the lab has for this pair: `lab` = both have curves (sparklines + rows), `scored` =
 * at least one score axis on both sides, `specs` = spec sheet only.
 */
export type CompareTier = 'lab' | 'scored' | 'specs'

export interface Comparison {
  tier: CompareTier
  /** What the card prints at a glance: Sound, ANC, battery. */
  rows: CompareRow[]
  /** Behind the "Details" toggle: isolation, the differing specs (worst first). */
  details: CompareRow[]
  /** Spec labels (with value) identical on both sides — one grey line, never a row each. */
  same: string[]
}

/** Score axes in row order, each on its filter scale (mirrors FEATURED_SCORE_DEFAULTS).
 *  `key` is the row's stable id; `keys` the fold keys either side may carry it under. The
 *  declared chip (`axisScore`) and the product-line derivation (`compare-source.ts`) read it too. */
export const SCORE_AXES: ReadonlyArray<{
  key: string
  keys: string[]
  label: string
  min: number
  max: number
  /** Whether a one-sided score gets an "absent" row (Isolation is too niche to call out). */
  callOut: boolean
  /** Printed at a glance, or only behind "Details". */
  detail: boolean
}> = [
  {
    key: 'sound',
    keys: ['mdaqs_overall'],
    label: 'Sound',
    min: 1,
    max: 5,
    callOut: true,
    detail: false,
  },
  {
    key: 'anc',
    keys: ['active_noise_cancelling', 'noise_cancelling'],
    label: 'ANC',
    min: 0,
    max: 10,
    callOut: true,
    detail: false,
  },
  {
    key: 'isolation',
    keys: ['isolation', 'isolation_attenuation'],
    label: 'Isolation',
    min: 0,
    max: 10,
    callOut: false,
    detail: true,
  },
]

/** Spec keys the battery row already covers — never a second row for them. */
const BATTERY_KEYS = new Set([
  'battery.battery_life',
  'battery.playtime_h',
  'battery.battery_life_wireless',
  'battery.battery_life_earbuds',
])

/** Numeric specs where LESS is better for the reader. */
const LESS_IS_BETTER = new Set(['design.weight', 'build.weight'])

/** Differences below this are noise, not a signal — shown as "same". */
const SCORE_EPSILON = 0.05
const HOURS_EPSILON = 0.5

function num(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function direction(diff: number, epsilon: number, invert = false): Direction {
  if (Math.abs(diff) < epsilon) {
    return 'same'
  }
  const up = invert ? diff < 0 : diff > 0
  return up ? 'up' : 'down'
}

function firstAxis(axes: Record<string, number> | null, keys: string[]): number | null {
  for (const k of keys) {
    const v = num(axes?.[k])
    if (v !== null) {
      return v
    }
  }
  return null
}

/** One product's score on a `SCORE_AXES` axis (by `key`) with that axis's label and scale;
 *  null when the product carries none. */
export function axisScore(
  axes: Record<string, number> | null,
  key: string
): { label: string; value: number; max: number } | null {
  const axis = SCORE_AXES.find(a => a.key === key)
  const value = axis ? firstAxis(axes, axis.keys) : null
  return axis && value !== null ? { label: axis.label, value, max: axis.max } : null
}

export function hoursText(h: number): string {
  const rounded = Math.abs(h) >= 10 ? Math.round(h) : Math.round(h * 10) / 10
  return `${rounded} h`
}

/** "+0.4" / "−0.8" with a true minus sign, one decimal. */
export function signedScore(diff: number): string {
  return `${diff > 0 ? '+' : ''}${diff.toFixed(1).replace('-', '−')}`
}

/** Phone rendering of a scale row: "3.9 vs 4.4 · −0.5 /5". */
export function scaleRowText(row: ScaleRow): string {
  return `${row.theirs.toFixed(1)} vs ${row.mine.toFixed(1)} · ${signedScore(
    row.theirs - row.mine
  )} /${row.max}`
}

/** A compare chip's display value: "60 h", "LDAC", "Yes" (the value, then its unit). */
export function specText(spec: Pick<CompareSpec, 'value' | 'unit'>): string {
  if (typeof spec.value === 'boolean') {
    return spec.value ? 'Yes' : 'No'
  }
  const unit = spec.unit ? ` ${spec.unit}` : ''
  return `${spec.value}${unit}`
}

function listOf(spec: CompareSpec): string[] | null {
  if (Array.isArray(spec.values)) {
    return spec.values.map(String)
  }
  return null
}

/** "IPX4" / "IP55" → 4 / 5 (the water digit); null when the string carries none. */
function ipDigit(value: string): number | null {
  const m = /^IP[X\d](\d)/i.exec(value.trim())
  return m ? Number(m[1]) : null
}

/** "none" / "no" — an enum value that means the feature is absent. */
const isNone = (value: string): boolean => /^(none|no)$/i.test(value.trim())

/** "2 devices" → 2 — a count with a unit word; a bare "5.3" (a version) is NOT a count. */
function leadingNumber(value: string): number | null {
  const m = /^\s*(\d+(?:\.\d+)?)\s*[a-z]/i.exec(value)
  return m ? Number(m[1]) : null
}

/**
 * Direction for two differing enum/text values: IP ratings on the water digit, "none" vs
 * anything as absent-vs-present, "2 devices" vs "none" on the leading number; anything else
 * is a difference, not a verdict.
 */
function textDirection(mine: string, theirs: string): Direction {
  const ipMine = ipDigit(mine)
  const ipTheirs = ipDigit(theirs)
  if (ipMine !== null && ipTheirs !== null) {
    const d = direction(ipTheirs - ipMine, 1e-9)
    return d === 'same' ? 'neutral' : d
  }
  if (isNone(theirs) !== isNone(mine)) {
    return isNone(theirs) ? 'down' : 'up'
  }
  const nMine = leadingNumber(mine)
  const nTheirs = leadingNumber(theirs)
  if (nMine !== null && nTheirs !== null && nMine !== nTheirs) {
    return nTheirs > nMine ? 'up' : 'down'
  }
  return 'neutral'
}

/** Rank for worst-first ordering of differing spec rows. */
const ORDER: Record<Direction, number> = { down: 0, neutral: 1, up: 2, same: 3 }

/**
 * One spec on both sides → a differing row, or the "same" label. Numbers compare on their
 * unit (more is better unless `LESS_IS_BETTER`), booleans prefer true, lists diff as sets,
 * IP ratings on their water digit; other text is neutral (a difference, not a verdict).
 */
function specRow(mine: CompareSpec, theirs: CompareSpec): CompareRow | string | null {
  const mineList = listOf(mine)
  const theirList = listOf(theirs)
  if (mineList && theirList) {
    const mineSet = new Set(mineList)
    const theirSet = new Set(theirList)
    const added = theirList.filter(v => !mineSet.has(v))
    const missing = mineList.filter(v => !theirSet.has(v))
    if (!added.length && !missing.length) {
      return `${theirs.label} ${theirList.join(', ')}`
    }
    const note = missing.length ? `yours adds ${missing.join(', ')}` : `adds ${added.join(', ')}`
    let dir: Direction = 'neutral'
    if (missing.length && !added.length) {
      dir = 'down'
    } else if (added.length && !missing.length) {
      dir = 'up'
    }
    return {
      kind: 'text',
      key: theirs.key,
      label: theirs.label,
      theirs: theirList.join(', '),
      mine: mineList.join(', '),
      direction: dir,
      note,
    }
  }
  if (typeof mine.value === 'boolean' && typeof theirs.value === 'boolean') {
    if (mine.value === theirs.value) {
      return `${theirs.label} ${specText(theirs)}`
    }
    return {
      kind: 'text',
      key: theirs.key,
      label: theirs.label,
      theirs: specText(theirs),
      mine: specText(mine),
      direction: theirs.value ? 'up' : 'down',
    }
  }
  const mineNum = typeof mine.value === 'number' ? mine.value : Number(mine.value)
  const theirNum = typeof theirs.value === 'number' ? theirs.value : Number(theirs.value)
  if (Number.isFinite(mineNum) && Number.isFinite(theirNum) && mine.dataType !== 'text') {
    const diff = theirNum - mineNum
    if (Math.abs(diff) < 1e-9) {
      return `${theirs.label} ${specText(theirs)}`
    }
    return {
      kind: 'text',
      key: theirs.key,
      label: theirs.label,
      theirs: specText(theirs),
      mine: specText(mine),
      direction: direction(diff, 1e-9, LESS_IS_BETTER.has(theirs.key)),
    }
  }
  const mineText = specText(mine)
  const theirText = specText(theirs)
  if (mineText.toLowerCase() === theirText.toLowerCase()) {
    return `${theirs.label} ${theirText}`
  }
  return {
    kind: 'text',
    key: theirs.key,
    label: theirs.label,
    theirs: theirText,
    mine: mineText,
    direction: textDirection(mineText, theirText),
  }
}

/**
 * The comparison a card prints against the reader's declared product. Rows in fixed order —
 * score axes, battery, differing specs (worst first, at most `specLimit`) — with
 * the identical specs collapsed into `same`. Empty rows + `specs` tier when nothing is
 * comparable.
 */
export function compareRows(declared: DeltaNumbers, card: DeltaNumbers, specLimit = 6): Comparison {
  const rows: CompareRow[] = []
  const details: CompareRow[] = []
  let scored = false
  const hasAny = (axes: Record<string, number> | null): boolean =>
    SCORE_AXES.some(a => firstAxis(axes, a.keys) !== null)
  const cardScored = hasAny(card.axes)
  const mineScored = hasAny(declared.axes)
  for (const axis of SCORE_AXES) {
    const mine = firstAxis(declared.axes, axis.keys)
    const theirs = firstAxis(card.axes, axis.keys)
    if (mine !== null && theirs !== null) {
      scored = true
      ;(axis.detail ? details : rows).push({
        kind: 'scale',
        key: axis.key,
        label: axis.label,
        mine,
        theirs,
        min: axis.min,
        max: axis.max,
        direction: direction(theirs - mine, SCORE_EPSILON),
      })
    } else if (mine !== null && cardScored && axis.callOut) {
      // The card was scored, just not on this axis — say so. An unscored card gets one
      // "not tested" line from the block instead of a "Not measured" row per axis.
      rows.push({ kind: 'absent', key: axis.key, label: axis.label, text: 'Not measured' })
    } else if (theirs !== null && mineScored && axis.callOut) {
      rows.push({
        kind: 'absent',
        key: axis.key,
        label: axis.label,
        text: `${theirs.toFixed(1)} /${axis.max}`,
        // Which side is missing, in the reader's words — and on its own full-width line, so
        // the phrase is never broken across a ~100px value column ("· not / measured").
        note: 'yours not measured',
      })
    }
  }

  const myHours = num(declared.batteryHours)
  const theirHours = num(card.batteryHours)
  if (myHours !== null && theirHours !== null) {
    rows.push({
      kind: 'text',
      key: 'battery',
      label: 'Battery',
      theirs: hoursText(theirHours),
      mine: hoursText(myHours),
      direction: direction(theirHours - myHours, HOURS_EPSILON),
    })
  }

  const same: string[] = []
  const specRows: TextRow[] = []
  const theirSpecs = new Map((card.compareSpecs ?? []).map(s => [s.key, s]))
  for (const mine of declared.compareSpecs ?? []) {
    if (BATTERY_KEYS.has(mine.key) && myHours !== null && theirHours !== null) {
      continue
    }
    const theirs = theirSpecs.get(mine.key)
    if (!theirs) {
      continue
    }
    const row = specRow(mine, theirs)
    if (typeof row === 'string') {
      same.push(row)
    } else if (row && row.kind === 'text') {
      specRows.push(row)
    }
  }
  specRows.sort((a, b) => ORDER[a.direction] - ORDER[b.direction])
  details.push(...specRows.slice(0, specLimit))

  const lab =
    num(declared.clearbuyProductId ?? null) !== null && num(card.clearbuyProductId ?? null) !== null
  let tier: CompareTier = 'specs'
  if (lab) {
    tier = 'lab'
  } else if (scored) {
    tier = 'scored'
  }
  return { tier, rows, details, same }
}

/** True when the product went through the SoundGuys lab: a ClearBuy id or a score axis.
 *  The declared product's side of this decides the "not lab-tested" disclosure (ticket
 *  "Versus yours without lab data", 2026-09-09) — said once, on the chip and the own tile. */
export function labTested(numbers: DeltaNumbers): boolean {
  if (num(numbers.clearbuyProductId ?? null) !== null) {
    return true
  }
  return Object.values(numbers.axes ?? {}).some(v => typeof v === 'number' && Number.isFinite(v))
}

/** "Battery Life With Case" → "Battery life with case"; an all-caps word (IP, ANC, USB) keeps
 *  its case. Schema labels arrive title-cased; a chip reads as a phrase, not a heading. */
export function sentenceCase(label: string): string {
  return label
    .split(' ')
    .map((word, i) => (i === 0 || /^[A-Z0-9]+$/.test(word) ? word : word.toLowerCase()))
    .join(' ')
}

/** True when `a` and `b` describe the same product (deltas against yourself are noise). */
export function sameProduct(
  a: { nodeId: string | null; slug: string | null },
  b: { nodeId: string | null; slug: string | null }
): boolean {
  if (a.nodeId && b.nodeId) {
    return a.nodeId === b.nodeId
  }
  return Boolean(a.slug) && a.slug === b.slug
}

// ── Curve verdicts ────────────────────────────────────────────────────────────

export interface Band {
  name: string
  lo: number
  hi: number
}

/** Bass / mids / treble, in Hz — the three bands a sparkline's one sentence is drawn from. */
export const BANDS: ReadonlyArray<Band> = [
  { name: 'bass', lo: 20, hi: 250 },
  { name: 'mids', lo: 250, hi: 4000 },
  { name: 'treble', lo: 4000, hi: 20000 },
]
/** Below this band-averaged difference the curves are "close to yours" — the card sentence,
 *  the dialog chips and the callout all use this one threshold, so no surface can call a pair
 *  "the same" while another prints a number for it. */
export const VERDICT_DB = 3
/** Share of a band's probes that must agree with the mean's sign before the mean is honest;
 *  below it the band is "mixed" — louder in places, quieter in others. */
const MIXED_AGREEMENT = 0.7
/** Log-spaced probe points per band. */
const PROBES = 24

export interface CurveVerdict {
  /** "+4 dB treble", "−9 dB bass", or "close to yours". */
  text: string
  /** Attenuation: more blocked = 'up'. Frequency response: louder is a difference, not a verdict → 'neutral'. */
  direction: Direction
  band: string | null
  db: number
}

/**
 * One sentence per chart: the band (bass/mids/treble) where THIS product differs most from
 * YOURS, as a mean over log-spaced probes of the nearest measured samples — never a single
 * point. Printed only above `VERDICT_DB`; otherwise "close to yours".
 */
export interface BandVerdict {
  band: Band
  /** Mean of THIS product minus YOURS over the band's probes. */
  db: number
  /** "+6 dB bass", "−1 dB mids", "0 dB treble". */
  text: string
  /** The largest single-probe difference in the band, signed — what the mean can hide. */
  widest: number
  /** Where `widest` sits, in Hz. */
  widestHz: number
  /** The mean is above `VERDICT_DB` but the sign flips across the band — louder in places,
   *  quieter in others — so "+3 dB" would be true and misleading at once. */
  mixed: boolean
}

/** Signed, rounded: "+6 dB", "−1 dB", "0 dB". */
export function dbText(db: number): string {
  const rounded = Math.round(db)
  if (rounded === 0) {
    return '0 dB'
  }
  return `${rounded > 0 ? '+' : '−'}${Math.abs(rounded)} dB`
}

/**
 * Every band's mean difference (this minus yours), in band order — what the compare dialog
 * lists as chips and shades on the chart. `curveVerdict` keeps the largest for the card.
 */
export function bandVerdicts(mine: Point[], theirs: Point[]): BandVerdict[] {
  if (mine.length < 2 || theirs.length < 2) {
    return []
  }
  return BANDS.map(band => {
    const lo = Math.log10(band.lo)
    const hi = Math.log10(band.hi)
    let sum = 0
    let positive = 0
    let widest = 0
    let widestHz = band.lo
    for (let i = 0; i < PROBES; i++) {
      const hz = 10 ** (lo + ((hi - lo) * (i + 0.5)) / PROBES)
      const d = nearestPoint(theirs, hz)[1] - nearestPoint(mine, hz)[1]
      sum += d
      if (d > 0) {
        positive += 1
      }
      if (Math.abs(d) > Math.abs(widest)) {
        widest = d
        widestHz = hz
      }
    }
    const db = sum / PROBES
    const agreement = db >= 0 ? positive / PROBES : 1 - positive / PROBES
    const mixed = Math.abs(db) >= VERDICT_DB && agreement < MIXED_AGREEMENT
    return { band, db, text: `${dbText(db)} ${band.name}`, widest, widestHz, mixed }
  })
}

// ── Band wording ──────────────────────────────────────────────────────────────
// The dialog's chips and callout say the same thing three ways: a number for scanning, a
// sentence with a subject so "this minus yours" never has to be decoded, and the spread the
// mean hides. All of it derives from one BandVerdict, so the surfaces cannot drift.

/** "20–250 Hz", "250 Hz–4 kHz", "4–20 kHz" — one unit when both edges share it. */
export function bandRangeText(band: Band): string {
  const lo = hzText(band.lo)
  const hi = hzText(band.hi)
  const unit = (t: string): string => t.slice(t.indexOf(' ') + 1)
  if (unit(lo) === unit(hi)) {
    return `${lo.slice(0, lo.indexOf(' '))}–${hi}`
  }
  return `${lo}–${hi}`
}

/** Capitalised band name: "Bass". */
export const bandLabel = (band: Band): string =>
  band.name.charAt(0).toUpperCase() + band.name.slice(1)

export type CurveKind = 'frequency' | 'attenuation'

/** Who the band sentences name: the primary curve and the curve it is measured against. The
 *  owner surfaces say "this one" / "yours"; `/compare` passes the two product names. */
export interface BandPair {
  this: string
  that: string
}
const THIS_VS_YOURS: BandPair = { this: 'this one', that: 'yours' }

/**
 * The chip's second line, a sentence with a subject: "+6 dB more than yours", "blocks 17 dB
 * more than yours", "about the same as yours" under the threshold.
 */
export function bandSentence(v: BandVerdict, kind: CurveKind, pair = THIS_VS_YOURS): string {
  if (Math.abs(v.db) < VERDICT_DB) {
    return `about the same as ${pair.that}`
  }
  const amount = `${Math.abs(Math.round(v.db))} dB`
  const more = v.db > 0 ? 'more' : 'less'
  if (kind === 'attenuation') {
    return `blocks ${amount} ${more} than ${pair.that}`
  }
  return `${dbText(v.db)} ${more} than ${pair.that}`
}

/** The callout headline on the chart: "Bass +6 dB on average", "Treble · about the same". */
export function bandHeadline(v: BandVerdict): string {
  if (Math.abs(v.db) < VERDICT_DB) {
    return `${bandLabel(v.band)} · about the same on average`
  }
  return `${bandLabel(v.band)} ${dbText(v.db)} on average`
}

/**
 * The callout's second line: the range, who leads, and the spread — the widest gap and where
 * it sits, or "louder in places, quieter in others" when the sign flips across the band.
 */
export function bandSpread(v: BandVerdict, kind: CurveKind, pair = THIS_VS_YOURS): string {
  const range = bandRangeText(v.band)
  const widest = `${Math.abs(Math.round(v.widest))} dB`
  if (Math.abs(v.db) < VERDICT_DB) {
    return Math.abs(v.widest) >= VERDICT_DB
      ? `${range} · but up to ${widest} apart near ${hzText(v.widestHz)}`
      : `${range} · the curves stay close all the way`
  }
  const who = v.db > 0 ? pair.this : pair.that
  const verb = kind === 'attenuation' ? 'blocks more here' : 'is louder here'
  if (v.mixed) {
    const [a, b] = kind === 'attenuation' ? ['blocks more', 'less'] : ['louder', 'quieter']
    return `${range} · ${a} in places, ${b} in others`
  }
  return `${range} · ${who} ${verb} · widest gap ${widest} near ${hzText(v.widestHz)}`
}

export function curveVerdict(
  mine: Point[],
  theirs: Point[],
  kind: 'frequency' | 'attenuation'
): CurveVerdict | null {
  const verdicts = bandVerdicts(mine, theirs)
  if (!verdicts.length) {
    return null
  }
  let best = verdicts[0]
  for (const v of verdicts) {
    if (Math.abs(v.db) > Math.abs(best.db)) {
      best = v
    }
  }
  if (Math.abs(best.db) < VERDICT_DB) {
    return { text: 'close to yours', direction: 'same', band: null, db: best.db }
  }
  let dir: Direction = 'neutral'
  if (kind === 'attenuation') {
    dir = Math.round(best.db) > 0 ? 'up' : 'down'
  }
  return { text: best.text, direction: dir, band: best.band.name, db: best.db }
}
